import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import { ChangeEvent, ComponentProps, ForwardedRef, forwardRef, ReactNode, Ref } from 'react';

import { Icon } from '../icon';
import { Divider } from '../divider';
import { InlineInput } from '../inline-input';

import styles from './input.module.scss';

const InputChrome = forwardRef((props: InputChromeProps, ref: ForwardedRef<HTMLLabelElement | HTMLDivElement>) => {
  const {
    left,
    right,
    error,
    value,
    field,
    message,
    disabled,
    readOnly,
    className,
    isDropdown,
    isTextarea,
    onClear,
    onClearValue,
    gap = 'small',
    size = 'medium',
    clearable = true,
    appearance = 'light',
    corderRadius = 'rounded',
    wrapperAs = 'label',
  } = props;

  const hasAction = !!(left || right) || isDropdown || clearable;
  const isClearable = clearable && appearance != 'minimal' && !!value && !readOnly;

  const fieldContent = (
    <>
      {hasAction && !!left && <div className={clsx(styles.inputActions)}>{left}</div>}
      {field}
      {hasAction && (!!right || isDropdown || isClearable) && (
        <div className={clsx(styles.inputActions, styles.rightActions)}>
          {isDropdown && <Icon size={14} type="arrow-down-01" />}
          {isClearable && (
            <button
              data-testid="clear-btn"
              className={styles.clearBtn}
              onClick={() => {
                onClear?.();
                onClearValue();
              }}
            >
              <Icon size={10} type="cancel-01" />
            </button>
          )}
          {right}
        </div>
      )}
    </>
  );

  const sharedProps = {
    title: typeof value === 'string' ? value : '',
    className: clsx(
      styles.inputHolder,
      styles[`g-${gap}`],
      styles[`s-${size}`],
      styles[`a-${appearance}`],
      styles[`cr-${corderRadius}`],
      {
        [styles.textarea]: isTextarea,
        [styles.error]: error,
        [styles.disabled]: disabled,
        [styles.readOnly]: readOnly,
      }
    ),
  };

  return (
    <div className={clsx(styles.inputContainer, className)}>
      {wrapperAs === 'div' ? (
        <div ref={ref as ForwardedRef<HTMLDivElement>} {...sharedProps}>
          {fieldContent}
        </div>
      ) : (
        <label ref={ref as ForwardedRef<HTMLLabelElement>} {...sharedProps}>
          {fieldContent}
        </label>
      )}
      {appearance === 'underline' && <Divider orientation="horizontal" className={styles.inputDivider} size="100%" />}
      <AnimatePresence>
        {error && !!message && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.25 }}
            className={styles.message}
          >
            {message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

const InputRoot = forwardRef((props: InputProps, ref: ForwardedRef<HTMLLabelElement | HTMLDivElement>) => {
  const {
    type,
    left,
    value,
    right,
    error,
    inline,
    message,
    inputRef,
    readOnly,
    disabled,
    className,
    inputProps,
    gap = 'small',
    size = 'medium',
    clearable = true,
    appearance = 'light',
    corderRadius = 'rounded',
    wrapperAs = 'label',
    onClear,
    ...rest
  } = props;

  const isDropdown = type === 'select';
  const isReadOnly = readOnly || isDropdown;

  return (
    <InputChrome
      ref={ref}
      left={left}
      right={right}
      error={error}
      value={value}
      message={message}
      disabled={disabled}
      readOnly={isReadOnly}
      className={className}
      isDropdown={isDropdown}
      onClear={onClear}
      gap={gap}
      size={size}
      clearable={clearable}
      appearance={appearance}
      corderRadius={corderRadius}
      wrapperAs={wrapperAs}
      onClearValue={() => {
        props.onChange?.({
          target: { value: '' },
        } as ChangeEvent<HTMLInputElement>);
      }}
      field={
        inline ? (
          <InlineInput
            type={type}
            value={value}
            ref={inputRef}
            disabled={disabled}
            readOnly={isReadOnly}
            className="ellipsis-text"
            {...inputProps}
            {...rest}
          />
        ) : (
          <input
            type={type}
            value={value}
            ref={inputRef}
            disabled={disabled}
            readOnly={isReadOnly}
            className="ellipsis-text"
            {...inputProps}
            {...rest}
          />
        )
      }
    />
  );
});

const InputTextarea = forwardRef((props: InputTextareaProps, ref: ForwardedRef<HTMLLabelElement | HTMLDivElement>) => {
  const {
    left,
    value,
    right,
    error,
    message,
    inputRef,
    readOnly,
    disabled,
    className,
    gap = 'small',
    size = 'medium',
    clearable = true,
    appearance = 'light',
    corderRadius = 'rounded',
    wrapperAs = 'label',
    onClear,
    ...rest
  } = props;

  return (
    <InputChrome
      ref={ref}
      left={left}
      right={right}
      error={error}
      value={value}
      message={message}
      disabled={disabled}
      readOnly={readOnly}
      className={className}
      isTextarea
      onClear={onClear}
      gap={gap}
      size={size}
      clearable={clearable}
      appearance={appearance}
      corderRadius={corderRadius}
      wrapperAs={wrapperAs}
      onClearValue={() => {
        props.onChange?.({
          target: { value: '' },
        } as ChangeEvent<HTMLTextAreaElement>);
      }}
      field={<textarea value={value} ref={inputRef} disabled={disabled} readOnly={readOnly} {...rest} />}
    />
  );
});

export const Input = Object.assign(InputRoot, { Textarea: InputTextarea });

type InputChromeSharedProps = {
  error?: boolean;
  left?: ReactNode;
  right?: ReactNode;
  message?: ReactNode;
  clearable?: boolean;
  onClear?: () => void;
  gap?: 'small' | 'medium';
  wrapperAs?: 'label' | 'div';
  corderRadius?: 'rounded' | 'smooth';
  size?: 'small' | 'medium' | 'big' | 'extra-large';
  appearance?: 'light' | 'filled' | 'filled-darker' | 'minimal' | 'underline' | 'outline';
};

export interface InputProps extends Omit<ComponentProps<'input'>, 'size' | 'ref'>, InputChromeSharedProps {
  inline?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  inputProps?: ComponentProps<'input'>;
}

export interface InputTextareaProps extends Omit<ComponentProps<'textarea'>, 'size' | 'ref'>, InputChromeSharedProps {
  inputRef?: Ref<HTMLTextAreaElement>;
}

type InputChromeProps = {
  error?: boolean;
  left?: ReactNode;
  right?: ReactNode;
  message?: ReactNode;
  clearable?: boolean;
  onClear?: () => void;
  gap?: 'small' | 'medium';
  wrapperAs?: 'label' | 'div';
  corderRadius?: 'rounded' | 'smooth';
  size?: 'small' | 'medium' | 'big' | 'extra-large';
  appearance?: 'light' | 'filled' | 'filled-darker' | 'minimal' | 'underline' | 'outline';
  className?: string;
  value?: string | number | readonly string[];
  disabled?: boolean;
  readOnly?: boolean;
  isDropdown?: boolean;
  isTextarea?: boolean;
  field: ReactNode;
  onClearValue: () => void;
};
